/**
 * App — autenticazione Phase 6 (IMPL-P6-008).
 *
 * Root SPA component: defines the React Router route table.
 *
 * Phase 6 completes the auth feature by wiring the remaining flows:
 *   - Forgot password   (`/password-dimenticata` + `/password-dimenticata/conferma`)
 *   - Reset password    (`/reimposta-password` + `/reimposta-password/successo`)
 *   - Role-aware dashboards under `RequireAuth` guards:
 *       /pratiche      (CLIENT)
 *       /lavorazione   (OPERATOR)
 *       /admin         (ADMIN)
 *
 * The catch-all `*` route still redirects to `/accedi` so unknown paths
 * land on the login screen instead of a blank page.
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import { SignupPage } from './features/auth/pages/SignupPage';
import { SignupConfirmPage } from './features/auth/pages/SignupConfirmPage';
import { LoginPage } from './features/auth/pages/LoginPage';
import { VerifyEmailPage } from './features/auth/pages/VerifyEmailPage';
import { ForgotPasswordPage } from './features/auth/pages/ForgotPasswordPage';
import { ForgotPasswordConfirmPage } from './features/auth/pages/ForgotPasswordConfirmPage';
import { ResetPasswordPage } from './features/auth/pages/ResetPasswordPage';
import { ResetPasswordSuccessPage } from './features/auth/pages/ResetPasswordSuccessPage';
import { LavorazioneDashboard } from './features/dashboard/pages/LavorazioneDashboard';
import { AdminDashboard } from './features/dashboard/pages/AdminDashboard';
import { ScadenzePage } from './features/admin/pages/ScadenzePage';
import { ProfiloPage } from './features/profilo/pages/ProfiloPage';
import { ArchivioPage } from './features/archivio/pages/ArchivioPage';
import { PraticaDetailPage as ArchivioPraticaDetailPage } from './features/archivio/pages/PraticaDetailPage';
import { NuovaPraticaPage } from './features/pratica/pages/NuovaPraticaPage';
import { PaymentSuccessPage } from './features/pratica/pages/PaymentSuccessPage';
import { PaymentFailedPage } from './features/pratica/pages/PaymentFailedPage';
import { AdminClientiPage } from './features/admin-wallet/pages/AdminClientiPage';
import { AdminUtenteDetailPage } from './features/admin-wallet/pages/AdminUtenteDetailPage';
import { AdminListinoPage } from './features/admin-listino';
import { AdminServiziPage } from './features/admin-servizi';
import { AdminReconciliationPage } from './features/admin-reconciliation';
import { AdminFatturePage } from './features/admin-fatture';
import { OperatorRoute } from './components/OperatorRoute';
import { RequireAuth } from './features/auth/components/RequireAuth';
import {
  OperatorQueuePage,
  OperatorPraticaDetailPage,
} from './features/operatore';
import {
  PraticheDashboardPage,
  PraticaDetailPage,
} from './features/pratiche-cliente/pages';
import { PraticheInCorsoPage } from './features/pratica-status/pages/PraticheInCorsoPage';
import { PraticaStatoDetailPage } from './features/pratica-status/pages/PraticaStatoDetailPage';
import { WalletPage } from './features/wallet';

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/accedi" replace />} />

      {/* Public auth routes */}
      <Route path="/registrati" element={<SignupPage />} />
      <Route path="/registrati/conferma" element={<SignupConfirmPage />} />
      <Route path="/accedi" element={<LoginPage />} />
      <Route path="/verifica-email" element={<VerifyEmailPage />} />
      <Route path="/password-dimenticata" element={<ForgotPasswordPage />} />
      <Route path="/password-dimenticata/conferma" element={<ForgotPasswordConfirmPage />} />
      <Route path="/reimposta-password" element={<ResetPasswordPage />} />
      <Route path="/reimposta-password/successo" element={<ResetPasswordSuccessPage />} />

      {/* Role-protected dashboards (RBAC enforced at the page boundary) */}
      {/* F-005: lista pratiche attive (CLIENT) — sostituisce il placeholder
          PraticheDashboard. Le sub-route specifiche (/stato, /correggi) sono
          montate PRIMA della catch-all `*`. */}
      <Route path="/pratiche" element={<PraticheInCorsoPage />} />
      <Route path="/pratiche/:id/stato" element={<PraticaStatoDetailPage />} />
      <Route path="/pratiche/:correctionOfId/correggi" element={<NuovaPraticaPage />} />
      <Route path="/lavorazione" element={<LavorazioneDashboard />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/scadenze" element={<ScadenzePage />} />

      {/* F-012: Admin backoffice — gestione fido wallet (guard ADMIN inside the page) */}
      <Route path="/admin/clienti" element={<AdminClientiPage />} />
      <Route path="/admin/clienti/:id" element={<AdminUtenteDetailPage />} />

      {/* F-009.4: Admin listino prezzi (tariffario per tipo pratica) */}
      <Route path="/admin/listino" element={<AdminListinoPage />} />

      {/* F-009.6: Admin catalogo servizi extra */}
      <Route path="/admin/servizi" element={<AdminServiziPage />} />

      {/* F-009.5: Admin riconciliazione pagamenti Stripe ↔ wallet */}
      <Route path="/admin/riconciliazione" element={<AdminReconciliationPage />} />

      {/* F-011 (rescoped): Admin coda fatture da emettere */}
      <Route path="/admin/fatture" element={<AdminFatturePage />} />

      {/* F-002: Profilo di fatturazione (qualunque ruolo autenticato) */}
      <Route path="/app/profilo" element={<ProfiloPage />} />

      {/* F-009.3: Wallet prepagato (saldo, ricarica Stripe, storico) */}
      <Route path="/app/wallet" element={<WalletPage />} />

      {/* F-006: Archivio permanente pratiche (lista + dettaglio) */}
      <Route path="/app/archivio" element={<ArchivioPage />} />
      <Route path="/app/archivio/:id" element={<ArchivioPraticaDetailPage />} />

      {/* F-003.2: Wizard pratica (PRIVATO) + dense form mount point */}
      <Route path="/app/pratiche/nuova" element={<NuovaPraticaPage />} />

      {/* F-010: ritorno da Stripe Checkout (pay-per-practice privati) */}
      <Route path="/app/pratiche/:id/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/app/pratiche/:id/payment-failed" element={<PaymentFailedPage />} />

      {/* F-005: Dashboard cliente (lista pratiche in corso) + dettaglio pratica.
          Wrapped in RequireAuth roles=['CLIENT'] per coerenza con /pratiche. */}
      <Route
        path="/app"
        element={
          <RequireAuth roles={['CLIENT']}>
            <PraticheDashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app/pratiche/:id"
        element={
          <RequireAuth roles={['CLIENT']}>
            <PraticaDetailPage />
          </RequireAuth>
        }
      />

      {/* F-007: dashboard operatore (coda + dettaglio pratica) */}
      <Route
        path="/operatore"
        element={
          <OperatorRoute>
            <OperatorQueuePage />
          </OperatorRoute>
        }
      />
      <Route
        path="/operatore/:id"
        element={
          <OperatorRoute>
            <OperatorPraticaDetailPage />
          </OperatorRoute>
        }
      />

      <Route path="*" element={<Navigate to="/accedi" replace />} />
    </Routes>
  );
}
